import { useState } from 'react'
import { PlusCircleOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { cx } from '../../components/ui/cx'
import { AddressFormModal } from './AddressFormModal'
import {
  addressFormFieldsFrom,
  formatAddressContactLine,
  formatShipToAddressLine,
} from './checkout-domain'
import type { AddressFormFields, AddressView } from './checkout-domain'

export interface ShippingAddressStepProps {
  readonly addresses: readonly AddressView[]
  readonly selectedId: string
  readonly onSelect: (id: string) => void
  readonly busy?: boolean
  readonly onCreate: (fields: AddressFormFields, isDefault?: boolean) => Promise<void>
  readonly onUpdate: (id: string, fields: AddressFormFields) => Promise<void>
  readonly onDelete: (id: string) => Promise<void>
  readonly onSetDefault: (id: string) => Promise<void>
}

/**
 * §1 of the single-page checkout (Stage-2b) — "Where should we ship these
 * parts?" Saved ship-to addresses as selectable cards
 * (`AddressView[]`, UID-97 K2 re-sourcing — see CheckoutPage's
 * `useAddressBook`), each showing the K4 contact name + company
 * ("Maya Chen · Northwind Robotics", prototype lines 1986-1987) and, unlike
 * the prior "coming in a follow-up" build, LIVE Edit / Set as default /
 * Delete actions (prototype lines 1988-1992) plus "+ Add new address"
 * (line 1996) that opens the Add/Edit modal (`AddressFormModal`, lines
 * 3791-3804).
 *
 * The action row is rendered OUTSIDE the selection `<label>` (a plain
 * sibling, not the shared `RadioCard`'s checked-only `children` slot) so
 * every card's actions stay visible regardless of which address is
 * currently selected — matching the prototype's unconditional per-row
 * actions (`Fictiv Platform.dc.html` lines 1988-1992 render inside the
 * `sc-for`, not gated on `a.sel`), and so clicking an action never toggles
 * selection as a side effect.
 */
export function ShippingAddressStep({
  addresses,
  selectedId,
  onSelect,
  busy = false,
  onCreate,
  onUpdate,
  onDelete,
  onSetDefault,
}: ShippingAddressStepProps) {
  const [addOpen, setAddOpen] = useState(false)
  const [editingId, setEditingId] = useState<string>()

  const editingAddress = addresses.find((address) => address.id === editingId)

  return (
    <>
      <fieldset className="checkout-address-fieldset" disabled={busy}>
        <legend className="sr-only">Shipping address</legend>
        {addresses.map((address) => {
          const checked = selectedId === address.id
          const contactLine = formatAddressContactLine(address)
          const radioId = `checkout-address-${address.id}`
          return (
            <div key={address.id} className={cx('radio-card', checked && 'radio-card-checked')}>
              <label htmlFor={radioId} className="checkout-address-card-select">
                <input
                  id={radioId}
                  type="radio"
                  name="shipping-address"
                  value={address.id}
                  checked={checked}
                  onChange={() => onSelect(address.id)}
                  disabled={busy}
                  className="radio-card-input"
                />
                <span className="radio-card-body">
                  <span className="radio-card-heading-row">
                    <span className="radio-card-label">{address.label}</span>
                    {address.isDefault && <Chip tone="brand">Default</Chip>}
                  </span>
                  {contactLine && <span className="radio-card-description">{contactLine}</span>}
                  <span className="radio-card-description">{formatShipToAddressLine(address)}</span>
                </span>
              </label>
              <div className="checkout-address-card-actions">
                <Button
                  variant="link"
                  size="sm"
                  className="checkout-edit-action"
                  aria-label={`Edit ${address.label}`}
                  onClick={() => setEditingId(address.id)}
                  disabled={busy}
                >
                  Edit
                </Button>
                {!address.isDefault && (
                  <Button
                    variant="link"
                    size="sm"
                    aria-label={`Set ${address.label} as default`}
                    onClick={() => void onSetDefault(address.id)}
                    disabled={busy}
                  >
                    Set as default
                  </Button>
                )}
                <Button
                  variant="link"
                  size="sm"
                  aria-label={`Delete ${address.label}`}
                  onClick={() => void onDelete(address.id)}
                  disabled={busy}
                >
                  Delete
                </Button>
              </div>
            </div>
          )
        })}
      </fieldset>
      <Button
        variant="outline"
        size="sm"
        className="checkout-add-address-btn"
        onClick={() => setAddOpen(true)}
        disabled={busy}
      >
        <PlusCircleOutlined aria-hidden="true" />
        Add new address
      </Button>

      <AddressFormModal open={addOpen} onClose={() => setAddOpen(false)} onSubmit={onCreate} />
      <AddressFormModal
        open={editingAddress !== undefined}
        onClose={() => setEditingId(undefined)}
        initialFields={editingAddress ? addressFormFieldsFrom(editingAddress) : undefined}
        onSubmit={(fields) => onUpdate(editingId as string, fields)}
      />
    </>
  )
}
